import { useEffect, useRef } from "react";
import { UseFormReturn, FieldValues } from "react-hook-form";

/**
 * Automatically resets a form when specified conditions are met. Only resets when shouldReset
 * transitions from false to true (dialog opens). Does not reset on defaultValues changes.
 */
export const useFormAutoReset = <T extends FieldValues>({
  form,
  defaultValues,
  shouldReset,
  hasData,
}: {
  form: UseFormReturn<T>;
  defaultValues: T;
  shouldReset: boolean;
  hasData: boolean;
}) => {
  const previousShouldResetRef = useRef(shouldReset);
  const defaultValuesRef = useRef(defaultValues);

  // Keep defaultValuesRef up to date without triggering the effect
  useEffect(() => {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);

  useEffect(() => {
    if (!hasData) {
      previousShouldResetRef.current = shouldReset;
      return;
    }

    // Dialog/form opens: reset form to defaultValues
    // Only reset when shouldReset transitions from false to true
    if (shouldReset && !previousShouldResetRef.current) {
      form.reset(defaultValuesRef.current);
    }

    // Dialog/form closes: reset form to defaultValues
    // Reset when shouldReset transitions from true to false
    if (!shouldReset && previousShouldResetRef.current) {
      form.reset(defaultValuesRef.current);
    }

    previousShouldResetRef.current = shouldReset;
  }, [shouldReset, hasData, form]);
};
